import {
    DeliveryType,
    IDeliveryTypeFormSectionProps,
    IDeliveryForm,
} from "../../../../Types/MeetingPlanning/deliveryForm"
import {
    IServiceCenterList,
    ResourceType
} from "../../../../Types/MeetingPlanning/userResources"
import { useToken } from "../../../Login/useToken"
import { useSelector } from "react-redux"
import { selectUser } from "../../../User/userSelectors"
import { selectCurrentEnvironment } from "../../../Environment/envSelectors"
import { useFormikContext } from "formik"
import React, { memo, useCallback, useEffect, useMemo, useState } from "react"
import { useQuery } from "../../../Query/useQuery"
import { Grid, makeStyles, Typography } from "@material-ui/core"
import { LocalizedStrict } from "../../../Localization/Components/Localized"
import { FormikAutocompleteControl, FormikTextField } from "../../../../Components/Form/Formik"
import { Localized, useLocalization } from "@fluent/react"
import Button from "../../../../Components/Button"
import { TEXT_FIELD_MAX_LENGTH, OTHER_LOCATION_TRANSLATE_KEY } from "../../../../Static/MeetingPlanning/deliveryForm"
import { NumberSelectControl } from "../../../../Components/Form/NumberSelectControl"
import { fetchResources } from "../../Api/MeetingPlanningApi"

const useDeliveryFormStyles = makeStyles((theme) => ({
    mr_2: {
        marginRight: theme.spacing(2),
    },
    mb_2: {
        marginBottom: theme.spacing(2),
    },
}))

const DeliveryTypeFormSection = ({ userCanteenId, isInitialShow }: IDeliveryTypeFormSectionProps) => {
    const token = useToken()
    const { l10n } = useLocalization()
    const user = useSelector(selectUser)
    const environment = useSelector(selectCurrentEnvironment)
    const classes = useDeliveryFormStyles()
    const { values, setFieldValue } = useFormikContext<IDeliveryForm>()
    const [choseOtherLocation, setChoseOtherLocation] = useState(false)

    useEffect(() => {
        if (values.hasOtherDeliveryLocation) { onChoseOtherLocation() }
    }, [values.hasOtherDeliveryLocation])

    useEffect(() => {
        const price = Array.isArray(values.extraServices)
            ? values.extraServices.reduce((sum, s) => sum + (s?.amount ?? 0), 0)
            : 0;
        setFieldValue("price", price)
    }, [values.extraServices, setFieldValue])

    useEffect(() => {
        if (values.deliveryType !== DeliveryType.DELIVERY) {
            setFieldValue("deliveryLocation", "")
            setFieldValue("otherDeliveryLocation", "")
        }
    }, [values.deliveryType])

    useEffect(() => {
        const isOther = values.deliveryLocation === OTHER_LOCATION_TRANSLATE_KEY
        if (!isOther) {
            setChoseOtherLocation(false)
            setFieldValue("otherDeliveryLocation", "")
        }

        if (isOther) {
            setChoseOtherLocation(true)
        }
    }, [values.deliveryLocation])

    useEffect(() => {
        if (values.otherDeliveryLocation) {
            setFieldValue("deliveryLocation", OTHER_LOCATION_TRANSLATE_KEY)
        }
    }, [values.otherDeliveryLocation])

    const shouldQuery = useMemo(() =>
            Boolean(token && userCanteenId && environment),
        [token, userCanteenId, environment]
    )

    const userId = user?.id ?? null

    const resourcesQuery = useQuery(
        () => fetchResources(token, userId!, environment, ResourceType.ROOM),
        [token, userCanteenId, environment],
        Boolean(userId)
    )

    const mapResourcesLocationOptions = useCallback((respData: IServiceCenterList) => {
        const roomOptions = respData.serviceCenters
            .flatMap((center) => center.resources)
            .map((resource) => ({
                label: resource.displayName,
                value: resource.displayName,
            }))

        return [
            ...roomOptions,
            {
                label: l10n.getString(OTHER_LOCATION_TRANSLATE_KEY),
                value: l10n.getString(OTHER_LOCATION_TRANSLATE_KEY),
            },
        ]
    }, [l10n])

    const onChoseOtherLocation = useCallback(() => {
        setChoseOtherLocation(true)
        setFieldValue("hasOtherDeliveryLocation", true)
    }, [choseOtherLocation, setFieldValue])

    const updateSeats = useCallback((value: number) => {
        setFieldValue("seats", value)
        setFieldValue("extraServices", values.extraServices.map((service) => {
            const seatPrice = service.basePrice * value
            const min = service.mininumPrice?.amount ?? 0
            const max = service.maximumPrice?.amount ?? Number.MAX_SAFE_INTEGER

            const clampedPrice = Math.min(Math.max(seatPrice, min), max)

            return {
                ...service,
                amount: clampedPrice
            }
        }))
    }, [values.extraServices, setFieldValue])

    if (!values.deliveryType) { return null }

    return (
        <>
            {isInitialShow ? <>
                {values?.deliveryType === DeliveryType.DELIVERY ? <Grid item className={classes.mb_2}>
                    <Grid item className={classes.mb_2}>
                        <LocalizedStrict id="delivery-form-delivery-location-field-label" attrs={{ label: true }}>
                            <resourcesQuery.QueryPane>
                                {(data) => {
                                    const options = mapResourcesLocationOptions(data)
                                    return <FormikAutocompleteControl name="deliveryLocation" label="Leveringssted" options={options} />
                                }}
                            </resourcesQuery.QueryPane>
                        </LocalizedStrict>
                    </Grid>
                    {choseOtherLocation
                        ? <Grid item>
                            <LocalizedStrict id="delivery-form-delivery-other-location-field-label" attrs={{ label: true }}>
                                <FormikTextField<IDeliveryForm>
                                    name="otherDeliveryLocation"
                                    inputProps={{ maxLength: TEXT_FIELD_MAX_LENGTH }}
                                    variant="outlined"
                                    fullWidth
                                    label="Mødenavn"
                                />
                            </LocalizedStrict>
                        </Grid>
                        : <Localized id="delivery-form-delivery-other-location-field-button">
                            <Button width="100%" onClick={onChoseOtherLocation}>+ Andet leveringssted</Button>
                        </Localized>
                    }
                </Grid> : null}
            </> : null}
            <Grid item className={classes.mb_2}>
                <Localized id="delivery-form-delivery-time-field-label">
                    <Typography variant="body1">
                        Leveringstid
                    </Typography>
                </Localized>
            </Grid>
            {isInitialShow ? <Grid item className={classes.mb_2}>
                <LocalizedStrict id="delivery-form-delivery-seats-field-label" attrs={{ label: true }}>
                    <NumberSelectControl name="seats" label="Kuverter" />
                </LocalizedStrict>
            </Grid> : null}
        </>
    )
}

export default memo(DeliveryTypeFormSection)
